import { useEffect, useState } from "react"
import { Panel } from "../Panel"
import type { MarketTickerRow, MarketDataSection, ItemPrediction } from "../dashboard-types"
import { useChat } from "../store"
import { Sparkline } from "./Sparkline"

type CategoryKey = "energy" | "freight_indices" | "container_rates" | "currencies" | "macro" | "logistics"

const TABS: { key: CategoryKey; label: string }[] = [
  { key: "energy", label: "Energy" },
  { key: "freight_indices", label: "Freight" },
  { key: "container_rates", label: "Container" },
  { key: "currencies", label: "Currency" },
  { key: "macro", label: "Macro" },
  { key: "logistics", label: "Logistics" },
]

interface Props {
  rows: MarketTickerRow[]
  marketData?: MarketDataSection
  predictions?: ItemPrediction[]
  demo?: boolean
}

function PredictionBadge({ pred }: { pred: ItemPrediction }) {
  const [showTooltip, setShowTooltip] = useState(false)
  const arrow = pred.direction === "up" ? "\u2191" : pred.direction === "down" ? "\u2193" : "\u2192"
  const sign = pred.predicted_change_pct >= 0 ? "+" : ""
  // Cost context: up = red (bad for shippers), down = green
  const color = pred.direction === "up"
    ? "text-red-400"
    : pred.direction === "down"
      ? "text-emerald-400"
      : "text-[color:var(--text-muted)]"

  const bgColor = pred.direction === "up"
    ? "bg-red-500/10"
    : pred.direction === "down"
      ? "bg-emerald-500/10"
      : "bg-[color:var(--surface-alt)]/50"

  return (
    <span
      className="relative inline-flex"
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      <span
        className={`inline-flex items-center gap-0.5 px-1 py-0 rounded text-[8px] tabular-nums font-mono cursor-default ${color} ${bgColor}`}
      >
        {arrow} {sign}{pred.predicted_change_pct.toFixed(1)}% 7d
      </span>
      {showTooltip && pred.drivers.length > 0 && (
        <span className="absolute z-50 left-0 bottom-full mb-1 w-52 p-2 rounded bg-[#1a1a2e] border border-[color:var(--border)] shadow-lg text-[9px] text-[color:var(--text-muted)] leading-relaxed pointer-events-none">
          <span className="block text-[8px] uppercase tracking-wider text-[color:var(--text-dim)] mb-1">
            Key Drivers ({(pred.confidence * 100).toFixed(0)}% confidence)
          </span>
          {pred.drivers.slice(0, 2).map((d, i) => (
            <span key={i} className="block mt-0.5">
              <span className={`inline-block w-1.5 h-1.5 rounded-full mr-1 ${
                d.impact === "high" ? "bg-red-400" : d.impact === "medium" ? "bg-amber-400" : "bg-sky-400"
              }`} />
              {d.factor}
            </span>
          ))}
        </span>
      )}
    </span>
  )
}

export function MarketTicker({ rows, marketData, predictions, demo }: Props) {
  const [activeTab, setActiveTab] = useState<CategoryKey>("energy")

  // LLM set_panel_filter support: `category` switches the active tab
  // (panel_id "market", e.g. filter_value "energy" | "freight_indices" | …).
  const filters = useChat((s) => s.panelFilters["market"])
  useEffect(() => {
    const cat = filters?.["category"]
    if (cat && TABS.some((t) => t.key === cat)) {
      setActiveTab(cat as CategoryKey)
    }
  }, [filters])

  // Build prediction lookup
  const predMap = new Map<string, ItemPrediction>()
  if (predictions) {
    for (const p of predictions) {
      predMap.set(p.item_id, p)
    }
  }

  // If we have the rich market_data, use it; otherwise fall back to legacy rows.
  const hasRichData = !!marketData
  const activeRows: MarketTickerRow[] = hasRichData
    ? (marketData[activeTab] ?? [])
    : rows

  const totalItems = hasRichData
    ? Object.values(marketData).reduce((sum, arr) => sum + arr.length, 0)
    : rows.length

  return (
    <Panel
      title="MARKET DATA"
      live
      demo={demo}
      count={totalItems}
      helpText="Comprehensive market data: energy, freight indices, container rates, currencies, macro indicators, and logistics metrics. Includes 7-day predictive forecasts."
    >
      {hasRichData && (
        <div className="flex items-center gap-0.5 px-2 py-1.5 border-b border-[color:var(--border)] overflow-x-auto">
          {TABS.map((tab) => {
            const count = marketData[tab.key]?.length ?? 0
            const isActive = activeTab === tab.key
            return (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center gap-1 px-2 py-1 rounded text-[9px] uppercase tracking-wider whitespace-nowrap transition-colors ${
                  isActive
                    ? "bg-[color:var(--accent)]/20 text-[color:var(--text)]"
                    : "text-[color:var(--text-muted)] hover:text-[color:var(--text)] hover:bg-[color:var(--surface-alt)]/50"
                }`}
              >
                {tab.label}
                <span className={`text-[8px] px-1 py-0 rounded-full tabular-nums ${
                  isActive
                    ? "bg-[color:var(--accent)]/30 text-[color:var(--text)]"
                    : "bg-[color:var(--surface-alt)] text-[color:var(--text-dim)]"
                }`}>
                  {count}
                </span>
              </button>
            )
          })}
        </div>
      )}
      <div className="grid grid-cols-2">
        {activeRows.map((r) => {
          const up = r.change_pct > 0
          const flat = r.change_pct === 0
          const color = flat ? "text-[color:var(--text-muted)]" : up ? "text-red-400" : "text-emerald-400"
          const glyph = flat ? "\u2192" : up ? "\u25B2" : "\u25BC"
          const sparkStroke = flat ? "#8a8a8a" : up ? "#ef4444" : "#10b981"
          const pred = predMap.get(r.id)
          return (
            <div key={r.id} className="px-3 py-1.5 border-r border-b border-[color:var(--border)]">
              <div className="text-[9px] uppercase tracking-widest text-[color:var(--text-dim)]">{r.label}</div>
              <div className="mt-0.5 flex items-baseline gap-2">
                <span className="text-base tabular-nums text-[color:var(--text)] font-semibold">
                  {r.value.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                </span>
                {r.unit && (
                  <span className="text-[9px] text-[color:var(--text-dim)] uppercase">{r.unit}</span>
                )}
              </div>
              <div className="mt-0.5 w-full">
                <Sparkline
                  values={r.history ?? []}
                  height={22}
                  stroke={sparkStroke}
                  forecast={pred?.forecast_7d}
                  forecastLow={pred?.forecast_low}
                  forecastHigh={pred?.forecast_high}
                />
              </div>
              <div className="mt-0.5 flex items-center gap-1.5">
                <span className={`text-xs tabular-nums ${color}`}>
                  {glyph} {Math.abs(r.change_pct).toFixed(2)}%
                </span>
                {pred && <PredictionBadge pred={pred} />}
              </div>
            </div>
          )
        })}
      </div>
    </Panel>
  )
}
